import React, { useEffect, useState } from "react";
import { useHistory } from "react-router";
import { DatePicker, Form, Input, notification, Select, Skeleton } from "antd";
import moment from "moment/moment";
import { Option } from "antd/lib/mentions";
import TextArea from "antd/lib/input/TextArea";
import { createFormItems, TYPE } from "./formItemSchema";
import { PageMetaTags } from "../../../../components/common";
import {
  getInformationNotes,
  saveInformationNotes,
  getAdvisorsDropdown
} from "../../../../services/destination";
import { isAnyFieldEditable } from "../common";
import { STUDENT } from "../../../../constants/rolesSubtypes";
import { MomentDateFormats } from "../../../../constants/dateFormats";
import {
  renderDestinationTrackerBreadcrumbs,
  renderStudentMetaData
} from "../common.jsx";
import HeaderWrapper from "../../../common/ProLayout/HeaderWrapper";

const DATE_FORMAT = MomentDateFormats.CustomDateFormat.dayMonthYear;
const ADVISER_FIELD = "careersInc_adviser";
const DATE_FIELDS = [
  "careers_interview_date_1",
  "careers_interview_date_2",
  "careers_interview_date_3"
];

const isObject = value =>
  value !== null && typeof value === "object" && !Array.isArray(value);

const mergeFormItemsWithApiData = (formItems, apiData) => {
  if (!apiData || Object.keys(apiData).length === 0) {
    return formItems;
  }

  const mergedItems = { ...formItems };

  Object.keys(apiData).forEach(key => {
    if (
      Object.prototype.hasOwnProperty.call(mergedItems, key) &&
      isObject(mergedItems[key]) &&
      isObject(apiData[key])
    ) {
      mergedItems[key] = {
        ...mergedItems[key],
        ...apiData[key]
      };
      return;
    }

    mergedItems[key] = apiData[key];
  });

  return mergedItems;
};

const getAdviserName = informationNotesData => {
  if (
    !informationNotesData ||
    !informationNotesData.careersInc_adviser_name ||
    !informationNotesData.careersInc_adviser_name.value
  ) {
    return null;
  }

  return informationNotesData.careersInc_adviser_name.value;
};

const getAdviserDisplayValue = (item, informationNotesData) => {
  if (item.name !== ADVISER_FIELD) {
    return item.value;
  }

  const adviserName = getAdviserName(informationNotesData);
  if (adviserName) {
    return adviserName;
  }

  return item.value;
};

const getDatePickerDefaultValue = itemValue => {
  if (!itemValue) {
    return null;
  }

  return moment(itemValue, DATE_FORMAT);
};

const getSelectOptionsWithAdviserFallback = (item, informationNotesData) => {
  if (item.name !== ADVISER_FIELD) {
    return item.options || [];
  }

  const adviserName = getAdviserName(informationNotesData);
  const options = item.options || [];
  const hasMatchingOption =
    Array.isArray(options) &&
    options.some(option => String(option.value) === String(item.value));

  if (
    !adviserName ||
    item.value === null ||
    item.value === undefined ||
    item.value === "" ||
    hasMatchingOption
  ) {
    return options;
  }

  return [{ value: item.value, label: adviserName }, ...options];
};

const createInformationNotesPayload = (formValues, studentId) => {
  const payload = { ...formValues };

  DATE_FIELDS.forEach(field => {
    if (payload[field]) {
      payload[field] = payload[field].format(DATE_FORMAT);
    }
  });

  if (studentId) {
    // `student_id` is required when GET/PUT from `adviser`.
    payload.student_id = studentId;
  }

  return payload;
};

const getAdvertiseList = studentId =>
  getAdvisorsDropdown({
    body: {},
    query: { student_id: studentId }
  })
    .then(res => {
      return Array.isArray(res) ? res : [];
    })
    .catch(() => []);

function InformationAndNotes({ user }) {
  const pageHeading = "Information & Notes";
  const [informationNotesData, setInformationNotesData] = useState(null);
  const [editable, setEditable] = useState(true);
  const [loading, setLoading] = useState(false);
  const [getLoading, setGetLoading] = useState(false);
  const [form] = Form.useForm();
  const history = useHistory();
  const query = new URLSearchParams(history.location.search);
  const studentId = query.get("id");

  useEffect(() => {
    setGetLoading(true);
    const payload = {};
    const fallbackFormItems = createFormItems();
    let adviserOptionsPromise = Promise.resolve([]);
    if (studentId) {
      payload.student_id = studentId;
      adviserOptionsPromise = getAdvertiseList(studentId);
    }

    Promise.all([
      getInformationNotes({ query: payload }),
      adviserOptionsPromise
    ])
      .then(([response, adviserOptions]) => {
        const baseFormItems = createFormItems(adviserOptions);
        if (response.success && response.status_code === 200) {
          const isEditable =
            isAnyFieldEditable(response.data) &&
            Boolean(response.data && response.data["form_visible"]);
          const newData = mergeFormItemsWithApiData(
            baseFormItems,
            response.data
          );
          setEditable(isEditable);
          setInformationNotesData(newData);
        } else {
          setEditable(true);
          setInformationNotesData(baseFormItems);
        }
      })
      .catch(() => {
        setEditable(true);
        setInformationNotesData(fallbackFormItems);
      })
      .finally(() => setGetLoading(false));
  }, [studentId]);

  const onFinish = formValues => {
    setLoading(true);
    const payload = createInformationNotesPayload(formValues, studentId);

    saveInformationNotes({ body: payload })
      .then(() => {
        notification.success({ message: `Saved the ${pageHeading}` });
      })
      .catch(() => {
        notification.error({
          message: `Failed to save ${pageHeading}`
        });
      })
      .finally(() => setLoading(false));
  };

  const renderAsLabel = item => {
    const renderedValue = getAdviserDisplayValue(item, informationNotesData);

    return (
      <div className="col-12 col-md-6">
        <div className="form-group pb-2">
          <label className="labelstyle">{item.label}</label>
          <Form.Item name={item.name}>
            <Input
              data-cy={item.name}
              defaultValue={renderedValue}
              readOnly={true}
              placeholder={item.placeholder}
            />
          </Form.Item>
        </div>
      </div>
    );
  };

  const renderAsTextareaLabel = item => {
    return (
      <div className="col-12 col-md-6">
        <div className="form-group pb-2">
          <label className="labelstyle">{item.label}</label>
          <Form.Item name={item.name}>
            <TextArea
              data-cy={item.name}
              className="textarea-style"
              readOnly={true}
              defaultValue={item.value}
              autoSize={{ minRows: 5, maxRows: 8 }}
            />
          </Form.Item>
        </div>
      </div>
    );
  };

  const renderDateElement = item => {
    if (!item) {
      return null;
    }

    if (!item.editable || !informationNotesData["form_visible"]) {
      return (
        <div className="col-12 col-md-6">
          <div className="form-group pb-2">
            <label className="labelstyle">{item.label}</label>
            <Form.Item name={item.name}>
              <DatePicker
                data-cy={item.name}
                format={DATE_FORMAT}
                defaultValue={getDatePickerDefaultValue(item.value)}
                disabled
                placeholder={item.placeholder}
                suffixIcon={null}
              />
            </Form.Item>
          </div>
        </div>
      );
    }

    if (!item.value && item.defaultValue) {
      return (
        <div className="col-12 col-md-6">
          <div className="form-group pb-4">
            <label className="labelstyle">{item.label}</label>
            <Form.Item name={item.name}>
              <DatePicker
                data-cy={item.name}
                format={DATE_FORMAT}
                className={"w-100"}
                defaultValue={moment(item.defaultValue)}
              />
            </Form.Item>
          </div>
        </div>
      );
    }

    return (
      <div className="col-12 col-md-6">
        <div className="form-group pb-4">
          <label className="labelstyle">{item.label}</label>
          <Form.Item name={item.name}>
            <DatePicker
              data-cy={item.name}
              format={DATE_FORMAT}
              className={"w-100"}
              defaultValue={getDatePickerDefaultValue(item.value)}
            />
          </Form.Item>
        </div>
      </div>
    );
  };

  const renderInputElement = item => {
    if (!item) {
      return null;
    }
    if (!item.editable || !informationNotesData["form_visible"]) {
      return renderAsLabel(item);
    }

    return (
      <div className="col-12 col-md-6">
        <div className="form-group pb-2">
          <label className="labelstyle">{item.label}</label>
          <Form.Item name={item.name}>
            <Input
              data-cy={item.name}
              defaultValue={item.value}
              disabled={!item.editable}
              placeholder={item.placeholder}
            />
          </Form.Item>
        </div>
      </div>
    );
  };

  const renderSelectElement = item => {
    if (!item) {
      return null;
    }
    if (!item.editable || !informationNotesData["form_visible"]) {
      return renderAsLabel(item);
    }
    const optionsWithFallback = getSelectOptionsWithAdviserFallback(
      item,
      informationNotesData
    );

    return (
      <div className="col-12 col-md-6">
        <div className="form-group pb-4">
          <label className="labelstyle">{item.label}</label>
          <Form.Item name={item.name}>
            <Select
              data-cy={item.name}
              showSearch
              className={"w-100"}
              allowClear={true}
              style={{ width: 200 }}
              placeholder={item.placeholder}
              optionFilterProp="children"
              disabled={!item.editable}
              defaultValue={item.value}
              filterOption={(input, option) =>
                String(option.children)
                  .toLowerCase()
                  .indexOf(input.toLowerCase()) >= 0
              }
            >
              <Option disabled>Select</Option>
              {optionsWithFallback.map(option => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </div>
      </div>
    );
  };

  const renderTextAreaElement = item => {
    if (!item) {
      return null;
    }
    if (!item.editable || !informationNotesData["form_visible"]) {
      return renderAsTextareaLabel(item);
    }

    return (
      <div className="col-12 col-md-6">
        <label className="labelstyle">{item.label}</label>
        <Form.Item name={item.name}>
          <TextArea
            data-cy={item.name}
            className="textarea-style"
            rows={5}
            disabled={!item.editable}
            defaultValue={item.value}
          />
        </Form.Item>
      </div>
    );
  };

  const renderSubHeadingText = () => {
    return (
      <div className="mb-2">
        <p>
          Throughout your time in education you will have the opportunity to
          meet with a careers adviser. These meetings are all about you and the
          plans you need to put in place to prepare for when you leave
          education. Take a look at the following statements and see which you
          identify with most...
        </p>
        <ul className="bullets" type="1">
          <li key="0">
            I have no idea what I want to do or be. It’s worrying that people
            keep asking me
          </li>
          <li key="1">
            It’s too early to think about a career. I need to focus on my exams
          </li>
          <li key="2">
            I don’t even understand what I can do when I leave school
          </li>
          <li key="3">I want to go to Uni</li>
          <li key="4">I think an apprenticeship might be good for me</li>
        </ul>
        <p>
          It doesn’t matter what your starting point is, your careers
          appointment is an opportunity to discuss your ambitions, your hopes
          and also the things that may be preventing you from doing what you
          really want to do. During the meeting you will agree an action plan
          which sets out your personal goals and support to achieve them.
        </p>
      </div>
    );
  };

  const renderField = item => {
    if (!isObject(item) || !item.visibile) {
      return null;
    }

    switch (item.type) {
      case TYPE.date:
        return renderDateElement(item);
      case TYPE.input:
        return renderInputElement(item);
      case TYPE.select:
        return renderSelectElement(item);
      case TYPE.textArea:
        return renderTextAreaElement(item);
      default:
        return null;
    }
  };

  return (
    <div data-cy="page-information-notes" className="content-panel">
      {studentId && renderStudentMetaData(studentId)}
      {studentId && renderDestinationTrackerBreadcrumbs(studentId)}
      {user.role_slug === STUDENT ? (
        <HeaderWrapper title={pageHeading} />
      ) : (
        <>
          <PageMetaTags title={pageHeading} />
          <h1 className="page-heading">{pageHeading}</h1>
        </>
      )}

      {user && user.role_slug === STUDENT && renderSubHeadingText()}

      <div className="profile-wrapper">
        <Skeleton loading={getLoading}>
          <Form
            data-cy="form-destination-tracker-information-notes"
            form={form}
            onFinish={onFinish}
          >
            <div className="row">
              {informationNotesData &&
                Object.keys(informationNotesData).map(key => (
                  <React.Fragment key={key}>
                    {renderField(informationNotesData[key])}
                  </React.Fragment>
                ))}

              {/*Submit*/}
              {editable && (
                <div className="col-12 mt-4">
                  <div className="form-group pb-4">
                    <input
                      type="submit"
                      className="button-style button-style-green-btm button-style-normal-button"
                      disabled={loading}
                      value={loading ? "Loading..." : "Submit"}
                    />
                  </div>
                </div>
              )}
            </div>
          </Form>
        </Skeleton>
      </div>
    </div>
  );
}

export { InformationAndNotes };
